import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';

/**
 * InteractiveReportViewer v3
 *
 * - Multi-level Group By with nested subtotals
 * - Ref-based drag (no re-renders during drag)
 * - Column customization panel (hide/show + add optional columns)
 * - Click-to-sort, hyperlinks, currency formatting
 * - Column pinning (right-click), row search, sticky header
 * - Grand total row
 */

// ─── Formatting ────────────────────────────────────────────────────────

const fmtCell = (value, colName, rules) => {
  if (value === null || value === undefined || value === '') return '';
  const r = rules?.[colName];
  if (!r) return String(value);
  switch (r.type) {
    case 'currency': { const n = parseFloat(value); return isNaN(n) ? String(value) : n.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: r.decimals ?? 2, maximumFractionDigits: r.decimals ?? 2 }); }
    case 'number': { const n = parseFloat(value); return isNaN(n) ? String(value) : n.toLocaleString('en-US', { minimumFractionDigits: r.decimals ?? 0, maximumFractionDigits: r.decimals ?? 0 }); }
    case 'percent': { const n = parseFloat(value); return isNaN(n) ? String(value) : (n * 100).toFixed(r.decimals ?? 1) + '%'; }
    default: return String(value);
  }
};

const cellUrl = (col, rules, row) => {
  const r = rules?.[col];
  return (r?.type === 'hyperlink' && r.urlKey) ? (row[r.urlKey] || null) : null;
};

const summable = (cols, rules) => cols.filter(c => { const r = rules?.[c]; return r?.type === 'currency' || r?.type === 'number'; });

const sortRows = (rows, cfg) => {
  if (!cfg) return rows;
  const { column: col, direction: dir } = cfg;
  return [...rows].sort((a, b) => {
    const an = parseFloat(a[col]), bn = parseFloat(b[col]);
    if (!isNaN(an) && !isNaN(bn)) return dir === 'asc' ? an - bn : bn - an;
    const cmp = String(a[col] ?? '').localeCompare(String(b[col] ?? ''), undefined, { numeric: true, sensitivity: 'base' });
    return dir === 'asc' ? cmp : -cmp;
  });
};

// ─── Multi-Level Grouping Engine ───────────────────────────────────────

const PATH_SEP = '\x00'; // Null byte separator — won't appear in data

const buildNestedGroups = (parentRows, detailMap, groupCols, level, pathPrefix, sumCols, sortCfg) => {
  const col = groupCols[level];
  const isLeaf = level === groupCols.length - 1;

  // Group rows by this column's value
  const buckets = new Map();
  for (const row of parentRows) {
    const key = String(row[col] ?? '(Blank)');
    if (!buckets.has(key)) buckets.set(key, []);
    buckets.get(key).push(row);
  }

  // Sort group keys
  const keys = [...buckets.keys()];
  if (sortCfg?.column === col) {
    keys.sort((a, b) => { const c = a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }); return sortCfg.direction === 'desc' ? -c : c; });
  } else {
    keys.sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
  }

  const rows = [];
  const meta = { detail: new Set(), header: new Set(), subtotal: new Set(), ranges: new Map() };

  for (const key of keys) {
    const groupParents = buckets.get(key);
    const path = pathPrefix ? pathPrefix + PATH_SEP + key : key;

    // Header
    const hIdx = rows.length;
    meta.header.add(hIdx);
    rows.push({ _gh: true, _level: level, _key: key, _path: path, _count: groupParents.length, _col: col });

    if (isLeaf) {
      // Leaf level — emit sorted data rows + detail children
      const sorted = (sortCfg && sortCfg.column !== col) ? sortRows(groupParents, sortCfg) : groupParents;
      for (const parent of sorted) {
        rows.push(parent);
        const children = detailMap.get(parent);
        if (children) { for (const ch of children) { meta.detail.add(rows.length); rows.push(ch); } }
      }
    } else {
      // Recurse deeper
      const inner = buildNestedGroups(groupParents, detailMap, groupCols, level + 1, path, sumCols, sortCfg);
      const offset = rows.length;
      for (const r of inner.rows) rows.push(r);
      // Offset inner meta indices
      for (const i of inner.meta.detail) meta.detail.add(i + offset);
      for (const i of inner.meta.header) meta.header.add(i + offset);
      for (const i of inner.meta.subtotal) meta.subtotal.add(i + offset);
      for (const [p, range] of inner.meta.ranges) meta.ranges.set(p, { hIdx: range.hIdx + offset, sIdx: range.sIdx + offset });
    }

    // Subtotal
    const sIdx = rows.length;
    meta.subtotal.add(sIdx);
    const sub = { _gs: true, _level: level, _key: key, _path: path, _col: col, _count: groupParents.length };
    for (const c of sumCols) {
      let s = 0; for (const p of groupParents) { const v = parseFloat(p[c]); if (!isNaN(v)) s += v; }
      sub[c] = Math.round(s * 100) / 100;
    }
    rows.push(sub);
    meta.ranges.set(path, { hIdx, sIdx });
  }

  return { rows, meta };
};

const buildGrandTotal = (parents, sumCols) => {
  const t = { _gt: true, _count: parents.length };
  for (const c of sumCols) {
    let s = 0; for (const r of parents) { const v = parseFloat(r[c]); if (!isNaN(v)) s += v; }
    t[c] = Math.round(s * 100) / 100;
  }
  return t;
};


// ─── Component ─────────────────────────────────────────────────────────

const InteractiveReportViewer = ({
  columns, data, formatRules = {}, detailRowIndices = null,
  summary = null, onDownloadExcel, onRefresh,
  loading = false, reportName = 'Report', lastRunTime = null,
  // Column names to hide by default (pre-fetched but not user-selected)
  defaultHiddenColumnNames = [],
}) => {
  const [columnOrder, setColumnOrder] = useState(() => columns.map((_, i) => i));
  const [sortConfig, setSortConfig] = useState(null);
  const [collapsedDetails, setCollapsedDetails] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [pinnedColumns, setPinnedColumns] = useState(new Set([0]));
  const [groupByColumns, setGroupByColumns] = useState([]); // ordered array of column names
  const [collapsedPaths, setCollapsedPaths] = useState(new Set());
  const [showGroupByPanel, setShowGroupByPanel] = useState(false);
  const [showColumnsPanel, setShowColumnsPanel] = useState(false);
  const [hiddenColumns, setHiddenColumns] = useState(new Set());

  // Refs for optimized drag (no re-renders during drag)
  const dragSrcRef = useRef(null);
  const dragOverRef = useRef(null);

  const groupByRef = useRef(null);
  const columnsPanelRef = useRef(null);
  const tableRef = useRef(null);

  // Reset on new report
  useEffect(() => {
    setColumnOrder(columns.map((_, i) => i));
    setSortConfig(null);
    setCollapsedDetails(new Set());
    setPinnedColumns(new Set([0]));
    setSearchTerm('');
    setGroupByColumns([]);
    setCollapsedPaths(new Set());
    // Initialize hidden columns from defaultHiddenColumnNames
    const hideSet = new Set();
    if (defaultHiddenColumnNames.length > 0) {
      const nameToIdx = new Map(columns.map((c, i) => [c, i]));
      for (const name of defaultHiddenColumnNames) {
        const idx = nameToIdx.get(name);
        if (idx !== undefined) hideSet.add(idx);
      }
    }
    setHiddenColumns(hideSet);
  }, [columns]);

  // Close panels on outside click
  useEffect(() => {
    const handler = (e) => {
      if (groupByRef.current && !groupByRef.current.contains(e.target)) setShowGroupByPanel(false);
      if (columnsPanelRef.current && !columnsPanelRef.current.contains(e.target)) setShowColumnsPanel(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const sumCols = useMemo(() => summable(columns, formatRules), [columns, formatRules]);

  // Text columns available for grouping
  const groupableColumns = useMemo(() => columns.filter(c => {
    const r = formatRules?.[c];
    return !(r?.type === 'currency' || r?.type === 'number' || r?.type === 'percent');
  }), [columns, formatRules]);

  // ─── Separate parents from detail rows ───────────────────────────────

  const { parentRows, detailMap } = useMemo(() => {
    const dSet = new Set(detailRowIndices || []);
    const parents = [], dMap = new Map();
    let cur = null;
    for (let i = 0; i < data.length; i++) {
      if (!dSet.has(i)) { cur = data[i]; parents.push(cur); dMap.set(cur, []); }
      else if (cur) { dMap.get(cur).push(data[i]); }
    }
    return { parentRows: parents, detailMap: dMap };
  }, [data, detailRowIndices]);

  // ─── Search filter ───────────────────────────────────────────────────

  const filteredParents = useMemo(() => {
    if (!searchTerm.trim()) return parentRows;
    const t = searchTerm.toLowerCase();
    return parentRows.filter(row => columns.some(c => {
      const v = row[c]; return v !== null && v !== undefined && String(v).toLowerCase().includes(t);
    }));
  }, [parentRows, searchTerm, columns]);

  // ─── Build display rows ──────────────────────────────────────────────

  const display = useMemo(() => {
    const hasGrouping = groupByColumns.length > 0;

    if (hasGrouping) {
      const { rows, meta } = buildNestedGroups(filteredParents, detailMap, groupByColumns, 0, '', sumCols, sortConfig);
      rows.push(buildGrandTotal(filteredParents, sumCols));
      return { rows, ...meta };
    }

    // Flat
    const sorted = sortConfig ? sortRows(filteredParents, sortConfig) : filteredParents;
    const rows = [], detail = new Set();
    for (const p of sorted) {
      rows.push(p);
      const ch = detailMap.get(p);
      if (ch) for (const c of ch) { detail.add(rows.length); rows.push(c); }
    }
    if (sumCols.length > 0 && filteredParents.length > 0) rows.push(buildGrandTotal(filteredParents, sumCols));
    return { rows, detail, header: new Set(), subtotal: new Set(), ranges: new Map() };
  }, [filteredParents, detailMap, groupByColumns, sumCols, sortConfig]);

  // ─── Parent-child map for detail expand/collapse ─────────────────────

  const parentGroupMap = useMemo(() => {
    const g = new Map(); let cur = null;
    for (let i = 0; i < display.rows.length; i++) {
      const r = display.rows[i];
      if (display.detail.has(i)) { if (cur !== null) g.get(cur)?.push(i); }
      else if (!display.header.has(i) && !display.subtotal.has(i) && !r._gt) { cur = i; g.set(i, []); }
    }
    return g;
  }, [display]);

  // ─── Collapse handlers ───────────────────────────────────────────────

  const toggleDetail = useCallback((idx) => {
    setCollapsedDetails(p => { const n = new Set(p); n.has(idx) ? n.delete(idx) : n.add(idx); return n; });
  }, []);

  const toggleGroupPath = useCallback((path) => {
    setCollapsedPaths(p => { const n = new Set(p); n.has(path) ? n.delete(path) : n.add(path); return n; });
  }, []);

  const expandAll = useCallback(() => { setCollapsedDetails(new Set()); setCollapsedPaths(new Set()); }, []);
  const collapseAll = useCallback(() => {
    setCollapsedDetails(new Set(parentGroupMap.keys()));
    setCollapsedPaths(new Set(display.ranges.keys()));
  }, [parentGroupMap, display.ranges]);

  // ─── Build visible rows ──────────────────────────────────────────────

  const visibleRows = useMemo(() => {
    const result = [];
    for (let i = 0; i < display.rows.length; i++) {
      const row = display.rows[i];

      if (row._gt) { result.push({ row, i, t: 'gt' }); continue; }

      if (display.header.has(i)) {
        const path = row._path;
        const collapsed = collapsedPaths.has(path);
        result.push({ row, i, t: 'gh', collapsed, path });
        if (collapsed) {
          // Skip to matching subtotal
          const range = display.ranges.get(path);
          if (range) i = range.sIdx - 1; // -1 because loop does i++
        }
        continue;
      }

      if (display.subtotal.has(i)) { result.push({ row, i, t: 'gs' }); continue; }

      if (display.detail.has(i)) {
        let pIdx = i - 1;
        while (pIdx >= 0 && display.detail.has(pIdx)) pIdx--;
        if (collapsedDetails.has(pIdx)) continue;
        result.push({ row, i, t: 'det' });
        continue;
      }

      const hasKids = parentGroupMap.has(i) && parentGroupMap.get(i).length > 0;
      result.push({ row, i, t: 'data', hasKids, collapsed: collapsedDetails.has(i) });
    }
    return result;
  }, [display, collapsedDetails, collapsedPaths, parentGroupMap]);

  // ─── Columns (with visibility filter) ────────────────────────────────

  const { pinnedCols, unpinnedCols } = useMemo(() => ({
    pinnedCols: columnOrder.filter(i => pinnedColumns.has(i) && !hiddenColumns.has(i)),
    unpinnedCols: columnOrder.filter(i => !pinnedColumns.has(i) && !hiddenColumns.has(i)),
  }), [columnOrder, pinnedColumns, hiddenColumns]);

  const allVisibleCols = useMemo(() => [...pinnedCols, ...unpinnedCols], [pinnedCols, unpinnedCols]);

  // ─── Optimized Drag & Drop (ref-based, no re-renders) ───────────────

  const clearDragIndicator = () => {
    if (dragOverRef.current !== null) {
      const el = tableRef.current?.querySelector('[data-drag-over="true"]');
      if (el) { el.style.borderLeft = ''; el.removeAttribute('data-drag-over'); }
      dragOverRef.current = null;
    }
  };

  const handleDragStart = useCallback((e, colIdx) => {
    dragSrcRef.current = colIdx;
    e.dataTransfer.effectAllowed = 'move';
    e.currentTarget.style.opacity = '0.4';
    const ghost = document.createElement('div');
    ghost.style.cssText = 'position:absolute;top:-999px;';
    ghost.textContent = columns[colIdx];
    document.body.appendChild(ghost);
    e.dataTransfer.setDragImage(ghost, 0, 0);
    requestAnimationFrame(() => document.body.removeChild(ghost));
  }, [columns]);

  const handleDragEnter = useCallback((e, colIdx) => {
    e.preventDefault();
    if (colIdx === dragSrcRef.current || colIdx === dragOverRef.current) return;
    clearDragIndicator();
    dragOverRef.current = colIdx;
    e.currentTarget.style.borderLeft = '3px solid #fbbf24';
    e.currentTarget.setAttribute('data-drag-over', 'true');
  }, []);

  const handleDragOver = useCallback((e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; }, []);

  const handleDrop = useCallback((e, targetIdx) => {
    e.preventDefault();
    clearDragIndicator();
    const srcIdx = dragSrcRef.current;
    if (srcIdx === null || srcIdx === targetIdx) { dragSrcRef.current = null; return; }
    setColumnOrder(prev => {
      const next = [...prev];
      const sp = prev.indexOf(srcIdx), tp = prev.indexOf(targetIdx);
      next.splice(sp, 1); next.splice(tp, 0, srcIdx);
      return next;
    });
    dragSrcRef.current = null;
  }, []);

  const handleDragEnd = useCallback((e) => {
    e.currentTarget.style.opacity = '';
    clearDragIndicator();
    dragSrcRef.current = null;
  }, []);

  // ─── Sorting ─────────────────────────────────────────────────────────

  const handleSort = useCallback((colName) => {
    setSortConfig(p => {
      if (!p || p.column !== colName) return { column: colName, direction: 'asc' };
      if (p.direction === 'asc') return { column: colName, direction: 'desc' };
      return null;
    });
  }, []);

  const sortInd = (col) => {
    if (!sortConfig || sortConfig.column !== col) return '\u21D5';
    return sortConfig.direction === 'asc' ? '\u2191' : '\u2193';
  };

  // ─── Pin ─────────────────────────────────────────────────────────────

  const togglePin = useCallback((ci) => {
    setPinnedColumns(p => { const n = new Set(p); n.has(ci) ? n.delete(ci) : n.add(ci); return n; });
  }, []);

  // ─── Group By Management ─────────────────────────────────────────────

  const addGroupLevel = useCallback((col) => {
    setGroupByColumns(p => p.includes(col) ? p : [...p, col]);
    setCollapsedPaths(new Set());
  }, []);

  const removeGroupLevel = useCallback((col) => {
    setGroupByColumns(p => p.filter(c => c !== col));
    setCollapsedPaths(new Set());
  }, []);

  const clearGrouping = useCallback(() => {
    setGroupByColumns([]);
    setCollapsedPaths(new Set());
  }, []);

  const moveGroupLevel = useCallback((fromIdx, toIdx) => {
    setGroupByColumns(p => {
      const n = [...p]; const [item] = n.splice(fromIdx, 1); n.splice(toIdx, 0, item); return n;
    });
    setCollapsedPaths(new Set());
  }, []);

  // ─── Column Visibility ───────────────────────────────────────────────

  const toggleColumnHidden = useCallback((ci) => {
    setHiddenColumns(p => { const n = new Set(p); n.has(ci) ? n.delete(ci) : n.add(ci); return n; });
  }, []);

  // ─── Add Optional Columns ────────────────────────────────────────────

  // ─── Stats ───────────────────────────────────────────────────────────

  const hasDetails = (detailRowIndices || []).length > 0;
  const hasGrouping = groupByColumns.length > 0;
  const hasExpandable = hasDetails || hasGrouping;

  // ─── Cell Renderer ───────────────────────────────────────────────────

  const renderCell = (row, ci, rType) => {
    const col = columns[ci];
    const val = row[col];

    if (rType === 'gs' || rType === 'gt') {
      if (sumCols.includes(col)) return fmtCell(val, col, formatRules);
      if (rType === 'gs' && col === row._col) return row._count + ' record' + (row._count !== 1 ? 's' : '');
      if (rType === 'gt' && ci === allVisibleCols[0]) return 'Grand Total (' + row._count + ' records)';
      return '';
    }

    const url = cellUrl(col, formatRules, row);
    const formatted = fmtCell(val, col, formatRules);
    if (url) return <a href={url} target="_blank" rel="noopener noreferrer" style={{ color: '#fbbf24', textDecoration: 'underline' }} title={'Open: ' + formatted}>{formatted}</a>;
    return formatted;
  };

  const bgFor = (type, n) => {
    switch (type) {
      case 'gh': return '#2a2040';
      case 'gs': return '#1e1e38';
      case 'gt': return '#2a2a10';
      case 'det': return '#1a1a2e';
      default: return n % 2 === 0 ? '#16162a' : '#1e1e32';
    }
  };

  // ─── Render Header Cell ──────────────────────────────────────────────

  const renderTh = (ci, isPinned, posIdx) => {
    const col = columns[ci];
    const r = formatRules?.[col];
    const isNum = r?.type === 'currency' || r?.type === 'number' || r?.type === 'percent';
    const leftOff = hasExpandable ? 32 : 0;
    return (
      <th key={(isPinned ? 'ph-' : 'uh-') + ci} draggable
        onDragStart={(e) => handleDragStart(e, ci)}
        onDragEnter={(e) => handleDragEnter(e, ci)}
        onDragOver={handleDragOver}
        onDrop={(e) => handleDrop(e, ci)}
        onDragEnd={handleDragEnd}
        onClick={() => handleSort(col)}
        onContextMenu={(e) => { e.preventDefault(); togglePin(ci); }}
        style={{
          ...S.th, cursor: 'grab',
          textAlign: isNum ? 'right' : 'left',
          ...(isPinned ? { ...S.pinnedTh, left: leftOff + posIdx * 150 } : {}),
        }}
        title={col + (isPinned ? ' (pinned)' : '') + ' \u00B7 Click sort \u00B7 Right-click pin'}>
        {isPinned && <span style={{ fontSize: '9px', marginRight: '4px' }}>{'\uD83D\uDCCC'}</span>}
        {col}
        <span style={S.sortInd}>{sortInd(col)}</span>
      </th>
    );
  };

  // ─── Render ──────────────────────────────────────────────────────────

  let dataNum = 0;

  return (
    <div style={S.container}>
      {/* ── Toolbar ── */}
      <div style={S.toolbar}>
        <div style={S.tLeft}>
          <span style={S.title}>{reportName}</span>
          {lastRunTime && <span style={S.ts}>Last run: {lastRunTime.toLocaleTimeString()}</span>}
          <span style={S.badge}>
            {filteredParents.length} records
            {searchTerm && ' (filtered)'}
            {hasGrouping && ' \u00B7 ' + groupByColumns.length + '-level grouping'}
          </span>
        </div>
        <div style={S.tRight}>
          {/* Search */}
          <div style={S.searchBox}>
            <span style={{ fontSize: '12px', marginRight: '4px', color: '#6b7280' }}>{'\uD83D\uDD0D'}</span>
            <input type="text" placeholder="Filter rows..." value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)} style={S.searchInput} />
            {searchTerm && <button onClick={() => setSearchTerm('')} style={S.clearBtn}>{'\u2715'}</button>}
          </div>

          {/* Group By */}
          <div ref={groupByRef} style={{ position: 'relative' }}>
            <button onClick={() => { setShowGroupByPanel(!showGroupByPanel); setShowColumnsPanel(false); }}
              style={{ ...S.actBtn, ...(hasGrouping ? { borderColor: '#fbbf24', color: '#fbbf24' } : {}) }}>
              {hasGrouping ? 'Grouped (' + groupByColumns.length + ')' : 'Group By'}
              <span style={{ marginLeft: '4px', fontSize: '10px' }}>{showGroupByPanel ? '\u25B2' : '\u25BC'}</span>
            </button>
            {showGroupByPanel && (
              <div style={S.dropdown}>
                {/* Current group levels */}
                {groupByColumns.length > 0 && (
                  <div style={{ padding: '8px 12px', borderBottom: '1px solid #2a2a40' }}>
                    <div style={{ fontSize: '10px', color: '#6b7280', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Active grouping (drag to reorder)</div>
                    {groupByColumns.map((col, idx) => (
                      <div key={col} draggable
                        onDragStart={(e) => { e.dataTransfer.setData('text/plain', idx); e.dataTransfer.effectAllowed = 'move'; }}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={(e) => { e.preventDefault(); const from = parseInt(e.dataTransfer.getData('text/plain')); if (!isNaN(from)) moveGroupLevel(from, idx); }}
                        style={{
                          display: 'flex', alignItems: 'center', gap: '6px', padding: '4px 8px',
                          marginBottom: '3px', backgroundColor: '#fbbf2415', borderRadius: '4px',
                          border: '1px solid #fbbf2430', cursor: 'grab', fontSize: '12px',
                        }}>
                        <span style={{ color: '#6b7280', fontSize: '10px', minWidth: '18px' }}>L{idx + 1}</span>
                        <span style={{ color: '#fbbf24', flex: 1 }}>{col}</span>
                        <span style={{ color: '#6b7280', fontSize: '10px', cursor: 'pointer' }}
                          onClick={() => removeGroupLevel(col)}>{'\u2715'}</span>
                      </div>
                    ))}
                    <button style={{ ...S.ddItem, color: '#ef4444', borderTop: '1px solid #2a2a40', marginTop: '4px', paddingTop: '6px' }}
                      onClick={() => { clearGrouping(); setShowGroupByPanel(false); }}>
                      {'\u2715'} Clear all grouping
                    </button>
                  </div>
                )}
                {/* Available columns to add */}
                <div style={{ padding: groupByColumns.length > 0 ? '4px 0' : '0' }}>
                  {groupByColumns.length > 0 && (
                    <div style={{ padding: '4px 12px', fontSize: '10px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Add level</div>
                  )}
                  {groupableColumns.filter(c => !groupByColumns.includes(c)).map(col => (
                    <button key={col} style={S.ddItem}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#2a2a40'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                      onClick={() => addGroupLevel(col)}>
                      {col}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Columns (show/hide) */}
          {columns.length > 2 && (
            <div ref={columnsPanelRef} style={{ position: 'relative' }}>
              <button onClick={() => { setShowColumnsPanel(!showColumnsPanel); setShowGroupByPanel(false); }}
                style={{ ...S.actBtn, ...(hiddenColumns.size > 0 ? { borderColor: '#f97316', color: '#f97316' } : {}) }}>
                Columns
                {hiddenColumns.size > 0 && <span style={{ marginLeft: '4px', fontSize: '10px' }}>({hiddenColumns.size} hidden)</span>}
              </button>
              {showColumnsPanel && (
                <div style={{ ...S.dropdown, minWidth: '260px', maxHeight: '400px' }}>
                  {/* Visible/hidden toggle */}
                  <div style={{ padding: '8px 12px', borderBottom: '1px solid #2a2a40' }}>
                    <div style={{ fontSize: '10px', color: '#6b7280', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Show/hide columns</div>
                    <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                      {columns.map((col, ci) => (
                        <label key={ci} style={{
                          display: 'flex', alignItems: 'center', gap: '6px', padding: '3px 0',
                          fontSize: '12px', color: hiddenColumns.has(ci) ? '#6b7280' : '#e5e7eb', cursor: 'pointer',
                        }}>
                          <input type="checkbox" checked={!hiddenColumns.has(ci)}
                            onChange={() => toggleColumnHidden(ci)}
                            style={{ accentColor: '#fbbf24' }} />
                          <span style={hiddenColumns.has(ci) ? { textDecoration: 'line-through' } : {}}>{col}</span>
                        </label>
                      ))}
                    </div>
                    {hiddenColumns.size > 0 && (
                      <button onClick={() => setHiddenColumns(new Set())}
                        style={{ marginTop: '6px', fontSize: '10px', color: '#fbbf24', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                        Show all columns
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Expand/Collapse All */}
          {hasExpandable && (
            <div style={{ display: 'flex', gap: '2px' }}>
              <button onClick={expandAll} style={S.smBtn}>{'\uFF0B'} All</button>
              <button onClick={collapseAll} style={S.smBtn}>{'\uFF0D'} All</button>
            </div>
          )}

          <button onClick={onRefresh} disabled={loading}
            style={{ ...S.actBtn, ...(loading ? S.disabled : {}) }}>
            {loading ? '\u27F3 Running...' : '\u27F3 Refresh'}
          </button>

          <button onClick={onDownloadExcel} disabled={loading}
            style={{ ...S.dlBtn, ...(loading ? S.disabled : {}) }}>
            {'\u2B07'} Download Excel
          </button>
        </div>
      </div>

      {/* ── Summary ── */}
      {summary?.lines?.length > 0 && (
        <div style={S.summaryBar}>
          {summary.lines.map((l, i) => <span key={i} style={{ whiteSpace: 'nowrap' }}>{l}</span>)}
        </div>
      )}

      {/* ── Hint ── */}
      <div style={S.hint}>
        Drag headers to reorder {'\u00B7'} Click to sort {'\u00B7'} Right-click to pin/unpin
      </div>

      {/* ── Table ── */}
      <div style={S.tableWrap} ref={tableRef}>
        <table style={S.table}>
          <thead>
            <tr>
              {hasExpandable && <th style={{ ...S.th, ...S.expCol, position: 'sticky', left: 0, zIndex: 12 }}></th>}
              {pinnedCols.map((ci, pi) => renderTh(ci, true, pi))}
              {unpinnedCols.map((ci) => renderTh(ci, false, 0))}
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((entry) => {
              const { row, i: idx, t: type } = entry;
              if (type === 'data') dataNum++;
              const bg = bgFor(type, dataNum);

              // ── Group Header ──
              if (type === 'gh') {
                const indent = (row._level || 0) * 20;
                const colSpan = pinnedCols.length + unpinnedCols.length;
                return (
                  <tr key={'gh-' + idx} style={{ backgroundColor: bg, borderTop: row._level === 0 ? '2px solid #fbbf24' : '1px solid #3b3b5c', cursor: 'pointer' }}
                    onClick={() => toggleGroupPath(entry.path)}>
                    {hasExpandable && (
                      <td style={{ ...S.td, ...S.expCol, position: 'sticky', left: 0, zIndex: 5, backgroundColor: bg }}>
                        <span style={{ color: '#fbbf24', fontSize: '10px' }}>{entry.collapsed ? '\u25B6' : '\u25BC'}</span>
                      </td>
                    )}
                    <td colSpan={colSpan} style={{ ...S.td, fontWeight: 700, fontSize: '13px', color: '#fbbf24', backgroundColor: bg, paddingLeft: 12 + indent + 'px' }}>
                      <span style={{ fontSize: '10px', color: '#6b7280', marginRight: '6px' }}>{row._col}:</span>
                      {row._key}
                      <span style={{ fontWeight: 400, fontSize: '11px', color: '#9ca3af', marginLeft: '10px' }}>
                        ({row._count} record{row._count !== 1 ? 's' : ''})
                      </span>
                    </td>
                  </tr>
                );
              }

              // ── All other row types ──
              const isSub = type === 'gs';
              const isGT = type === 'gt';
              const isDet = type === 'det';
              const isTotal = isSub || isGT;
              const indent = isSub ? (row._level || 0) * 20 : 0;
              const border = isGT ? { borderTop: '2px solid #fbbf24', borderBottom: '2px solid #fbbf24' }
                : isSub ? (row._level === 0 ? { borderBottom: '2px solid #3b3b5c' } : { borderBottom: '1px solid #2a2a40' })
                : {};

              return (
                <tr key={'r-' + idx} style={{ backgroundColor: bg, fontWeight: isTotal ? 700 : 400, ...border }}>
                  {hasExpandable && (
                    <td style={{ ...S.td, ...S.expCol, position: 'sticky', left: 0, zIndex: 5, backgroundColor: bg }}>
                      {entry.hasKids && (
                        <button onClick={() => toggleDetail(idx)} style={S.expBtn}>
                          {entry.collapsed ? '\u25B6' : '\u25BC'}
                        </button>
                      )}
                    </td>
                  )}
                  {pinnedCols.map((ci, pi) => {
                    const leftOff = hasExpandable ? 32 : 0;
                    return (
                      <td key={'p-' + ci} style={{
                        ...S.td, ...S.pinnedTd, left: leftOff + pi * 150, backgroundColor: bg,
                        ...(isDet ? { paddingLeft: '24px', fontSize: '11px', color: '#9ca3af' } : {}),
                        ...(isSub ? { paddingLeft: (12 + indent) + 'px' } : {}),
                      }}>
                        {renderCell(row, ci, type)}
                      </td>
                    );
                  })}
                  {unpinnedCols.map((ci) => {
                    const col = columns[ci];
                    const r = formatRules?.[col];
                    const isNum = r?.type === 'currency' || r?.type === 'number' || r?.type === 'percent';
                    return (
                      <td key={'u-' + ci} style={{
                        ...S.td, textAlign: isNum ? 'right' : 'left',
                        ...(isDet ? { fontSize: '11px', color: '#9ca3af' } : {}),
                      }}>
                        {renderCell(row, ci, type)}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
        {visibleRows.length === 0 && <div style={S.empty}>{searchTerm ? 'No rows match "' + searchTerm + '"' : 'No data'}</div>}
      </div>
    </div>
  );
};

// ─── Styles ────────────────────────────────────────────────────────────

const S = {
  container: { display: 'flex', flexDirection: 'column', height: '100%', backgroundColor: '#0f0f1e', color: '#e5e7eb', fontFamily: "'Segoe UI', -apple-system, sans-serif", fontSize: '13px', borderRadius: '8px', overflow: 'hidden', border: '1px solid #2a2a40' },
  toolbar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', backgroundColor: '#16162a', borderBottom: '1px solid #2a2a40', flexWrap: 'wrap', gap: '8px' },
  tLeft: { display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' },
  tRight: { display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' },
  title: { fontWeight: 700, fontSize: '15px', color: '#fbbf24' },
  ts: { fontSize: '11px', color: '#6b7280' },
  badge: { fontSize: '11px', color: '#9ca3af', backgroundColor: '#1e1e32', padding: '2px 8px', borderRadius: '10px' },
  searchBox: { display: 'flex', alignItems: 'center', backgroundColor: '#1e1e32', border: '1px solid #2a2a40', borderRadius: '6px', padding: '0 8px', height: '30px' },
  searchInput: { background: 'none', border: 'none', color: '#e5e7eb', fontSize: '12px', outline: 'none', width: '140px' },
  clearBtn: { background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', fontSize: '12px', padding: '0 2px' },
  smBtn: { backgroundColor: '#1e1e32', border: '1px solid #2a2a40', color: '#9ca3af', fontSize: '11px', padding: '4px 8px', borderRadius: '4px', cursor: 'pointer', whiteSpace: 'nowrap' },
  actBtn: { backgroundColor: '#1e1e32', border: '1px solid #3b3b5c', color: '#e5e7eb', fontSize: '12px', fontWeight: 600, padding: '5px 12px', borderRadius: '6px', cursor: 'pointer', whiteSpace: 'nowrap' },
  disabled: { opacity: 0.5, cursor: 'not-allowed' },
  dlBtn: { backgroundColor: '#fbbf24', border: 'none', color: '#0f0f1e', fontSize: '12px', fontWeight: 700, padding: '5px 14px', borderRadius: '6px', cursor: 'pointer', whiteSpace: 'nowrap' },
  dropdown: { position: 'absolute', top: '100%', right: 0, marginTop: '4px', backgroundColor: '#1e1e32', border: '1px solid #3b3b5c', borderRadius: '6px', boxShadow: '0 8px 24px rgba(0,0,0,0.4)', zIndex: 50, minWidth: '200px', maxHeight: '350px', overflowY: 'auto' },
  ddItem: { display: 'flex', alignItems: 'center', width: '100%', padding: '7px 12px', fontSize: '12px', color: '#e5e7eb', backgroundColor: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left', whiteSpace: 'nowrap' },
  summaryBar: { display: 'flex', flexWrap: 'wrap', gap: '8px 16px', padding: '8px 14px', backgroundColor: '#1a1a2e', borderBottom: '1px solid #2a2a40', fontSize: '11px', color: '#9ca3af' },
  hint: { padding: '4px 14px', fontSize: '10px', color: '#4b5563', borderBottom: '1px solid #1e1e32' },
  tableWrap: { flex: 1, overflow: 'auto', position: 'relative' },
  table: { width: '100%', borderCollapse: 'separate', borderSpacing: 0, tableLayout: 'auto' },
  th: { position: 'sticky', top: 0, zIndex: 10, backgroundColor: '#1e1e38', color: '#d1d5db', fontWeight: 600, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '8px 12px', textAlign: 'left', borderBottom: '2px solid #fbbf24', whiteSpace: 'nowrap', userSelect: 'none' },
  pinnedTh: { position: 'sticky', zIndex: 11, backgroundColor: '#1e1e38', boxShadow: '2px 0 4px rgba(0,0,0,0.3)' },
  sortInd: { fontSize: '10px', color: '#6b7280', marginLeft: '4px' },
  expCol: { width: '32px', minWidth: '32px', maxWidth: '32px', padding: '4px', textAlign: 'center' },
  td: { padding: '6px 12px', borderBottom: '1px solid #1e1e32', whiteSpace: 'nowrap', maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis' },
  pinnedTd: { position: 'sticky', zIndex: 5, boxShadow: '2px 0 4px rgba(0,0,0,0.2)' },
  expBtn: { background: 'none', border: 'none', color: '#fbbf24', cursor: 'pointer', fontSize: '10px', padding: '2px', lineHeight: 1 },
  empty: { textAlign: 'center', padding: '40px 20px', color: '#6b7280', fontSize: '14px' },
};

export default InteractiveReportViewer;